import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import { tokenIsExpired } from '../../../authUtils';
import {
    Refresh, FileDownload
} from '@mui/icons-material';
import {
    Button, Tooltip, Box, Typography, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
    Tabs, Tab
} from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const OutstandingReport = () => {
    const { isAuthenticated, companyId, branchId, token, logout, username, companyName } = useAuth();

    // Tab state
    const [activeTab, setActiveTab] = useState(0); // 0 = Vendor, 1 = Customer

    // Data states
    const [vendorOutstandingData, setVendorOutstandingData] = useState([]);
    const [customerOutstandingData, setCustomerOutstandingData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        if (companyId && token) {
            fetchOutstandingData();
        }
    }, [companyId, token]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    // Fetch outstanding data
    const fetchOutstandingData = async () => {
        setVendorOutstandingData([]);
        setCustomerOutstandingData([]);
        let loadingTimeout;
        setLoadingMessage('Loading outstanding data...');
        try {
            loadingTimeout = setTimeout(() => {
                setLoading(true);
            }, 300);

            const currentDate = getFormattedCurrentDate();

            // Fetch vendor outstanding
            const vendorResponse = await fetchData('ReportApi', `/VendorDuelist/${currentDate}/${companyId}/0`, token);
            if (vendorResponse) {
                if (vendorResponse.responseCode === '1111') {
                    setVendorOutstandingData(vendorResponse.response_data || []);
                } else if (Array.isArray(vendorResponse)) {
                    setVendorOutstandingData(vendorResponse);
                } else if (vendorResponse.response_data) {
                    setVendorOutstandingData(vendorResponse.response_data || []);
                }
            }

            // Fetch customer outstanding
            const customerResponse = await fetchData('ReportApi', `/CustomerDuelist/${currentDate}/${companyId}/0`, token);
            if (customerResponse) {
                if (customerResponse.responseCode === '1111') {
                    setCustomerOutstandingData(customerResponse.response_data || []);
                } else if (Array.isArray(customerResponse)) {
                    setCustomerOutstandingData(customerResponse);
                } else if (customerResponse.response_data) {
                    setCustomerOutstandingData(customerResponse.response_data || []);
                }
            }
        } catch (error) {
            console.error('Error fetching outstanding data:', error);
            setVendorOutstandingData([]);
            setCustomerOutstandingData([]);
        } finally {
            clearTimeout(loadingTimeout);
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        const data = activeTab === 0 ? vendorOutstandingData : customerOutstandingData;
        if (data.length === 0) return;

        const workbook = new ExcelJS.Workbook();
        const sheetName = activeTab === 0 ? 'Vendor Outstanding' : 'Customer Outstanding';
        const worksheet = workbook.addWorksheet(sheetName);
        worksheet.views = [
            { state: 'frozen', xSplit: 0, ySplit: 3 }
        ];

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const fileName = `${sheetName.replace(' ', '_')}_Report_${now.toLocaleDateString().replace(/\//g, '-')}.xlsx`;

        const thinBorder = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };

        // Company Name Row
        worksheet.mergeCells('A1:I1');
        const companyCell = worksheet.getCell('A1');
        companyCell.value = companyName || 'Company Name';
        companyCell.font = { bold: true, size: 14 };
        companyCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Title row
        worksheet.mergeCells('A2:I2');
        const titleCell = worksheet.getCell('A2');
        titleCell.value = `${sheetName} Report | Generated On: ${formattedDateTime} By: ${username}`;
        titleCell.font = { bold: true, size: 10 };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };

        // Header row
        const headers = activeTab === 0
            ? ['Sl.No', 'Purchase Id', 'Bill Date', 'Bill Number', 'Vendor Name', 'Purchase Amount', 'Paid Amount', 'Balance Amount', 'Due Days']
            : ['Sl.No', 'Sale Id', 'Bill Date', 'Bill Number', 'Customer Name', 'Sale Amount', 'Paid Amount', 'Balance Amount', 'Due Days'];
        const headerRow = worksheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7b3f8f' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = thinBorder;
        });

        // Calculate totals
        let totalAmount = 0;
        let totalPaidAmount = 0;
        let totalBalanceAmount = 0;

        // Add data rows
        data.forEach((rowData, index) => {
            const amount = parseFloat(activeTab === 0 ? rowData.PurchaseAmount : rowData.SaleAmount) || 0;
            const paidAmount = parseFloat(rowData.PaidAmount) || 0;
            const balanceAmount = parseFloat(rowData.BalanceAmount) || 0;

            totalAmount += amount;
            totalPaidAmount += paidAmount;
            totalBalanceAmount += balanceAmount;

            const row = worksheet.addRow([
                index + 1,
                activeTab === 0 ? (rowData.PurchaseId || '') : (rowData.SaleId || ''),
                rowData.BillDate ? formatDate(rowData.BillDate) : '',
                rowData.BillNumber || '',
                activeTab === 0 ? (rowData.VendorName || '') : (rowData.CustomerName || ''),
                amount,
                paidAmount,
                balanceAmount,
                rowData.DueDays || 0
            ]);

            row.eachCell((cell, colNumber) => {
                if (colNumber > 5) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    if (colNumber <= 8) {
                        cell.numFmt = '#,##0.00';
                    } else {
                        cell.numFmt = '#,##0';
                    }
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }
                cell.border = thinBorder;
            });
        });

        // Add total row
        const totalRow = worksheet.addRow([
            '', '', '', '', 'Total:',
            totalAmount,
            totalPaidAmount,
            totalBalanceAmount,
            ''
        ]);

        totalRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
            if (colNumber === 5) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            } else if (colNumber > 5 && colNumber <= 8) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
                cell.numFmt = '#,##0.00';
            }
            cell.border = thinBorder;
        });

        worksheet.columns = [
            { width: 8 },
            { width: 12 },
            { width: 15 },
            { width: 18 },
            { width: 30 },
            { width: 15 },
            { width: 15 },
            { width: 15 },
            { width: 12 }
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    // Calculate totals
    const calculateTotals = (data, isVendor = true) => {
        let totalAmount = 0;
        let totalPaidAmount = 0;
        let totalBalanceAmount = 0;

        data.forEach(item => {
            totalAmount += parseFloat(isVendor ? item.PurchaseAmount : item.SaleAmount) || 0;
            totalPaidAmount += parseFloat(item.PaidAmount) || 0;
            totalBalanceAmount += parseFloat(item.BalanceAmount) || 0;
        });

        return { totalAmount, totalPaidAmount, totalBalanceAmount };
    };

    // Filter data based on search query
    const currentData = activeTab === 0 ? vendorOutstandingData : customerOutstandingData;
    const filteredData = currentData.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const totals = calculateTotals(filteredData, activeTab === 0);

    return (
        <div style={{
            minWidth: '100%',
            background: '#f5f7fa',
            padding: isMobile ? '8px' : '10px',
            position: 'relative',
            paddingBottom: isMobile ? '80px' : '10px'
        }}>
            {/* Loading Overlay */}
            {loading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        zIndex: '99999 !important',
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress
                            size={60}
                            thickness={4}
                            sx={{
                                color: '#667eea',
                                mb: 2
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#667eea',
                                fontWeight: 600,
                                fontSize: '1rem'
                            }}
                        >
                            {loadingMessage}
                        </Typography>
                    </Box>
                </Box>
            )}

            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                padding: isMobile ? '8px' : '12px',
                position: 'relative',
                color: 'black',
                fontFamily: 'Times New Roman',
            }}>
                {/* Header Section */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    paddingBottom: '8px',
                    borderBottom: '2px solid #e0e0e0',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '8px' : '0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
                        <div style={{ width: '100%' }}>
                            <h4 style={{
                                fontSize: isMobile ? '15px' : '17px',
                                fontWeight: '700',
                                margin: 0,
                                letterSpacing: '-0.3px',
                                fontFamily: 'Times New Roman',
                                textTransform: 'uppercase',
                                textAlign: isMobile ? 'center' : 'left',
                                color: 'black'
                            }}>
                                Outstanding Report
                            </h4>
                        </div>
                    </div>
                </div>

                {/* Tabs and Filters Row */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                    gap: '10px',
                    flexWrap: isMobile ? 'wrap' : 'nowrap'
                }}>
                    {/* Tabs on the left */}
                    <Box sx={{ 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        flex: isMobile ? '1 1 100%' : '0 0 auto'
                    }}>
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            sx={{
                                minHeight: '35px',
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    minHeight: '35px',
                                    padding: '6px 16px'
                                },
                                '& .Mui-selected': {
                                    color: '#7b3f8f !important'
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#7b3f8f'
                                }
                            }}
                        >
                            <Tab label="Vendor Outstanding" />
                            <Tab label="Customer Outstanding" />
                        </Tabs>
                    </Box>

                    {/* Search and Buttons on the right */}
                    <div style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-end',
                        flex: isMobile ? '1 1 100%' : '0 0 auto',
                        justifyContent: 'flex-end'
                    }}>
                        {/* Search Input */}
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '2px', 
                            minWidth: isMobile ? '140px' : '180px'
                        }}>
                            <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>Search</label>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    width: '100%',
                                    borderRadius: '6px',
                                    backgroundColor: '#f8f9fa',
                                    fontSize: isMobile ? '11px' : '12px',
                                    height: '28px',
                                    border: '1px solid #d1d5db',
                                    padding: isMobile ? '4px 8px' : '4px 10px'
                                }}
                            />
                        </div>

                        {/* Buttons */}
                        {isMobile ? (
                            <>
                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={fetchOutstandingData}
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            width: '28px',
                                            height: '28px',
                                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                                            padding: '4px',
                                            marginTop: '18px'
                                        }}
                                    >
                                        <Refresh fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Export">
                                    <IconButton
                                        onClick={handleExportExcel}
                                        style={{
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            color: 'white',
                                            width: '28px',
                                            height: '28px',
                                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)',
                                            padding: '4px',
                                            marginTop: '18px'
                                        }}
                                    >
                                        <FileDownload fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            <>
                                <Button
                                    onClick={fetchOutstandingData}
                                    style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        padding: '4px 16px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                                        border: 'none',
                                        height: '28px',
                                        textTransform: 'none',
                                        letterSpacing: '0.3px',
                                        minWidth: '80px',
                                        marginTop: '18px'
                                    }}
                                >
                                    Refresh
                                </Button>

                                <Button
                                    onClick={handleExportExcel}
                                    startIcon={<FileDownload />}
                                    disabled={filteredData.length === 0}
                                    style={{
                                        background: filteredData.length > 0
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                            : '#ccc',
                                        color: 'white',
                                        padding: '4px 16px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        boxShadow: filteredData.length > 0
                                            ? '0 2px 8px rgba(16, 185, 129, 0.4)'
                                            : 'none',
                                        border: 'none',
                                        height: '28px',
                                        textTransform: 'none',
                                        letterSpacing: '0.3px',
                                        minWidth: '120px',
                                        marginTop: '18px'
                                    }}
                                >
                                    Export to Excel
                                </Button>
                            </>
                        )}
                    </div>
                </div>



                {/* Table Section */}
                <div style={{ marginBottom: isMobile ? '80px' : '10px' }}>
                    <TableContainer component={Paper} style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        borderRadius: '10px',
                        overflow: 'hidden'
                    }}>
                        <Table stickyHeader sx={{ minWidth: isMobile ? 1000 : 650 }} size="small">
                            <TableHead>
                                <TableRow style={{
                                    background: '#7b3f8f',
                                    height: '32px'
                                }}>
                                    {(activeTab === 0
                                        ? ['Sl.No', 'Purchase Id', 'Bill Date', 'Bill Number', 'Vendor Name', 'Purchase Amount', 'Paid Amount', 'Balance Amount', 'Due Days']
                                        : ['Sl.No', 'Sale Id', 'Bill Date', 'Bill Number', 'Customer Name', 'Sale Amount', 'Paid Amount', 'Balance Amount', 'Due Days']
                                    ).map((col) => (
                                        <TableCell
                                            key={col}
                                            style={{
                                                background: '#7b3f8f',
                                                color: 'white',
                                                fontWeight: '600',
                                                fontSize: isMobile ? '10px' : '11px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px',
                                                padding: isMobile ? '6px 8px' : '6px 12px',
                                                textAlign: col.includes('Sl.No') || col.includes('Id') || col.includes('Date') ? 'center' : col.includes('Number') || col.includes('Name') ? 'left' : 'right'
                                            }}
                                        >
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                                No outstanding records found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <>
                                        {filteredData.map((data, index) => (
                                            <TableRow
                                                key={index}
                                                sx={{
                                                    background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                    '&:hover': {
                                                        backgroundColor: '#e3f2fd !important',
                                                        transition: 'all 0.15s ease-in-out'
                                                    },
                                                    borderBottom: '1px solid #e0e0e0'
                                                }}
                                            >
                                                <TableCell style={{
                                                    fontSize: isMobile ? '11px' : '12px',
                                                    padding: isMobile ? '8px 8px' : '10px 12px',
                                                    color: '#2d3748',
                                                    textAlign: 'center'
                                                }}>
                                                    {index + 1}
                                                </TableCell>

                                                <TableCell style={{
                                                    fontSize: isMobile ? '11px' : '12px',
                                                    padding: isMobile ? '8px 8px' : '10px 12px',
                                                    color: '#2d3748',
                                                    textAlign: 'center'
                                                }}>
                                                    {activeTab === 0 ? (data.PurchaseId || '-') : (data.SaleId || '-')}
                                                </TableCell>

                                                <TableCell style={{
                                                    fontSize: isMobile ? '11px' : '12px',
                                                    padding: isMobile ? '8px 8px' : '10px 12px',
                                                    color: '#2d3748',
                                                    textAlign: 'center'
                                                }}>
                                                    {formatDate(data.BillDate)}
                                                </TableCell>

                                                <TableCell style={{
                                                    fontSize: isMobile ? '11px' : '12px',
                                                    padding: isMobile ? '8px 8px' : '10px 12px',
                                                    color: '#2d3748'
                                                }}>
                                                    {data.BillNumber || '-'}
                                                </TableCell>

                                                <TableCell style={{
                                                    fontSize: isMobile ? '11px' : '12px',
                                                    padding: isMobile ? '8px 8px' : '10px 12px',
                                                    color: '#2d3748'
                                                }}>
                                                    {activeTab === 0 ? (data.VendorName || '-') : (data.CustomerName || '-')}
                                                </TableCell>

                                                <TableCell style={{
                                                    fontSize: isMobile ? '11px' : '12px',
                                                    padding: isMobile ? '8px 8px' : '10px 12px',
                                                    color: '#2d3748',
                                                    textAlign: 'right'
                                                }}>
                                                    {(parseFloat(activeTab === 0 ? data.PurchaseAmount : data.SaleAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>

                                                <TableCell style={{
                                                    fontSize: isMobile ? '11px' : '12px',
                                                    padding: isMobile ? '8px 8px' : '10px 12px',
                                                    color: '#2d3748',
                                                    textAlign: 'right'
                                                }}>
                                                    {(parseFloat(data.PaidAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>

                                                <TableCell style={{
                                                    fontSize: isMobile ? '11px' : '12px',
                                                    padding: isMobile ? '8px 8px' : '10px 12px',
                                                    color: '#d32f2f',
                                                    textAlign: 'right',
                                                    fontWeight: '600'
                                                }}>
                                                    {(parseFloat(data.BalanceAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>

                                                <TableCell style={{
                                                    fontSize: isMobile ? '11px' : '12px',
                                                    padding: isMobile ? '8px 8px' : '10px 12px',
                                                    color: '#2d3748',
                                                    textAlign: 'right'
                                                }}>
                                                    {data.DueDays || '0'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* Total Row */}
                                        <TableRow
                                            sx={{
                                                backgroundColor: '#f0f0f0',
                                                borderTop: '2px solid #cbd5e1',
                                                '&:hover': {
                                                    backgroundColor: '#f0f0f0 !important'
                                                }
                                            }}
                                        >
                                            <TableCell colSpan={5} style={{
                                                fontSize: isMobile ? '11px' : '12px',
                                                padding: isMobile ? '10px 8px' : '10px 12px',
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#2d3748'
                                            }}>
                                                Total:
                                            </TableCell>

                                            <TableCell style={{
                                                fontSize: isMobile ? '11px' : '12px',
                                                padding: isMobile ? '10px 8px' : '10px 12px',
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#2d3748'
                                            }}>
                                                {totals.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>

                                            <TableCell style={{
                                                fontSize: isMobile ? '11px' : '12px',
                                                padding: isMobile ? '10px 8px' : '10px 12px',
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#2d3748'
                                            }}>
                                                {totals.totalPaidAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>

                                            <TableCell style={{
                                                fontSize: isMobile ? '11px' : '12px',
                                                padding: isMobile ? '10px 8px' : '10px 12px',
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#d32f2f'
                                            }}>
                                                {totals.totalBalanceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>

                                            <TableCell style={{
                                                fontSize: isMobile ? '11px' : '12px',
                                                padding: isMobile ? '10px 8px' : '10px 12px'
                                            }}>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </div>
        </div>
    );
};

export default OutstandingReport;
